import axios from 'axios';
import { ElMessage } from 'element-plus';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// 错误消息去重（3秒内相同消息只弹一次）
const recentMessages = new Map();
function dedupMessage(message, type = 'error') {
  const key = `${type}:${message}`;
  const now = Date.now();
  const last = recentMessages.get(key);
  if (last && now - last < 3000) return;
  recentMessages.set(key, now);
  // 清理过期记录
  for (const [k, v] of recentMessages) {
    if (now - v > 3000) recentMessages.delete(k);
  }
  if (type === 'error') ElMessage.error(message);
}

// 请求拦截器
api.interceptors.request.use(config => {
  const token = localStorage.getItem('eyc_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器
api.interceptors.response.use(
  response => {
    const data = response.data;
    // 文件下载
    if (response.config.responseType === 'blob') return response;
    if (data.code === 401) {
      localStorage.removeItem('eyc_token');
      localStorage.removeItem('eyc_user');
      window.location.hash = '#/login';
      return Promise.reject(new Error(data.message));
    }
    if (data.code !== 0 && data.code !== undefined) {
      dedupMessage(data.message || '操作失败');
      return Promise.reject(new Error(data.message));
    }
    return data;
  },
  error => {
    dedupMessage(error.response?.data?.message || '网络错误');
    return Promise.reject(error);
  }
);

export default api;

// === 认证 ===
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  registerSupplier: (data) => api.post('/auth/register-supplier', data),
  resubmitSupplier: (data) => api.post('/auth/resubmit-supplier', data),
  customersPublic: () => api.get('/auth/customers-public'),
  rejectReason: (username) => api.get('/auth/reject-reason', { params: { username } }),
  departmentsPublic: (params) => api.get('/auth/departments-public', { params }),
  categoriesPublic: () => api.get('/auth/categories-public'),
  uploadPublic: (file, prefix = 'qualifications') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('prefix', prefix);
    return api.post('/auth/upload-public', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getUserInfo: () => api.get('/auth/userinfo'),
  changePassword: (data) => api.post('/auth/change-password', data),
  changeUsername: (data) => api.post('/auth/change-username', data),
  captcha: () => api.get('/auth/captcha'),
  wechatAuthUrl: () => api.get('/auth/wechat-auth-url'),
  wechatLoginUrl: () => api.get('/auth/wechat-login-url'),
  wechatUnbind: () => api.get('/auth/wechat-unbind'),
  // PC 微信扫码登录
  wechatPcQrconnect: () => api.get('/auth/wechat-pc-qrconnect'),
  wechatPcBind: (data) => api.post('/auth/wechat-pc-bind', data),
};

// === 单位 ===
export const customerApi = {
  list: (params) => api.get('/customers', { params }),
  all: () => api.get('/customers/all'),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  remove: (id) => api.delete(`/customers/${id}`),
  setAuditMode: (id, auditMode) => api.patch(`/customers/${id}/audit-mode`, { auditMode }),
};

// === 科室 ===
export const departmentApi = {
  list: (params) => api.get('/departments', { params }),
  all: (params) => api.get('/departments/all', { params }),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  remove: (id) => api.delete(`/departments/${id}`),
  importDepartments: (data) => api.post('/departments/import', data),
};

// === 用户 ===
export const userApi = {
  list: (params) => api.get('/users', { params }),
  detail: (id) => api.get(`/users/${id}/detail`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  audit: (id, data) => api.post(`/users/${id}/audit`, data),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  remove: (id) => api.delete(`/users/${id}`),
};

// === 商品 ===
export const productApi = {
  list: (params) => api.get('/products', { params }),
  h5List: (params) => api.get('/products/h5', { params }),
  categories: () => api.get('/products/categories'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  remove: (id) => api.delete(`/products/${id}`),
  toggle: (id) => api.post(`/products/${id}/toggle`),
  clone: (id, data) => api.post(`/products/${id}/clone`, data),
  batchToggleStatus: (data) => api.post('/products/batch-toggle-status', data),
  batchRemove: (data) => api.post('/products/batch-remove', data),
  audit: (data) => api.post('/products/audit', data),
  importProducts: (data) => api.post('/products/import', data),
};

// === 购物车 ===
export const cartApi = {
  list: () => api.get('/cart'),
  add: (data) => api.post('/cart', data),
  update: (id, data) => api.put(`/cart/${id}`, data),
  remove: (id) => api.delete(`/cart/${id}`),
  clear: () => api.delete('/cart'),
};

// === 订单 ===
export const orderApi = {
  list: (params) => api.get('/orders', { params }),
  detail: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  audit: (id, data) => api.post(`/orders/${id}/audit`, data),
  batchAudit: (data) => api.post('/orders/batch-audit', data),
  deliver: (id) => api.post(`/orders/${id}/deliver`),
  delivered: (id) => api.post(`/orders/${id}/delivered`),
  getDeliveryQRCode: (id) => api.get(`/orders/${id}/delivery-qrcode`),
  batchWarehousePrint: (data) => api.post('/orders/batch-warehouse-print', data),
  sign: (id, data) => api.post(`/orders/${id}/sign`, data),
  assignDelivery: (id, data) => api.post(`/orders/${id}/assign-delivery`, data),
  cancel: (id, reason) => api.post(`/orders/${id}/cancel`, { reason }),
};

// === 供应商 ===
export const supplierApi = {
  list: (params) => api.get('/suppliers', { params }),
  all: () => api.get('/suppliers/all'),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  toggle: (id) => api.post(`/suppliers/${id}/toggle`),
  applyCustomer: (data) => api.post('/suppliers/apply-customer', data),
  myApplications: () => api.get('/suppliers/my-applications'),
  customerApplications: (params) => api.get('/suppliers/customer-applications', { params }),
  auditApplication: (id, data) => api.post(`/suppliers/audit-application/${id}`, data),
};

// === 经营品类字典 ===
export const categoryApi = {
  list: (params) => api.get('/categories', { params }),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  remove: (id) => api.delete(`/categories/${id}`),
  toggle: (id) => api.post(`/categories/${id}/toggle`),
};

// === 供应商年度采购额度 ===
export const quotaApi = {
  list: (params) => api.get('/quotas', { params }),
  create: (data) => api.post('/quotas', data),
  update: (id, data) => api.put(`/quotas/${id}`, data),
  remove: (id) => api.delete(`/quotas/${id}`),
  recalc: (id) => api.post(`/quotas/${id}/recalc`),
};

// === 统计 ===
export const statsApi = {
  dashboard: () => api.get('/stats/dashboard'),
  byDepartment: (params) => api.get('/stats/by-department', { params }),
  byProduct: (params) => api.get('/stats/by-product', { params }),
  exportOrders: (params) => api.get('/stats/export', { params, responseType: 'blob' }),
  exportSummary: (params) => api.get('/stats/export-summary', { params, responseType: 'blob' }),
};

// === 发票 ===
export const invoiceApi = {
  list: (params) => api.get('/invoices', { params }),
  unbilledOrders: (params) => api.get('/invoices/unbilled-orders', { params }),
  create: (data) => api.post('/invoices', data),
  detail: (id) => api.get(`/invoices/${id}`),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  confirm: (id, data) => api.post(`/invoices/${id}/confirm`, data),
  reject: (id, data) => api.post(`/invoices/${id}/reject`, data),
  stats: () => api.get('/invoices/stats/summary'),
  unbilledStats: (params) => api.get('/invoices/stats/unbilled', { params }),
  export: (params) => api.get('/invoices/export', { params, responseType: 'blob' }),
};

// 文件上传
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// === 开放接口凭证管理（超管） ===
export const apiClientApi = {
  list: (params) => api.get('/admin/api-clients', { params }),
  create: (data) => api.post('/admin/api-clients', data),
  update: (id, data) => api.put(`/admin/api-clients/${id}`, data),
  resetSecret: (id) => api.post(`/admin/api-clients/${id}/reset-secret`),
  enable: (id) => api.post(`/admin/api-clients/${id}/enable`),
  disable: (id) => api.post(`/admin/api-clients/${id}/disable`),
  logs: (id, params) => api.get(`/admin/api-clients/${id}/logs`, { params }),
  stats: (id) => api.get(`/admin/api-clients/${id}/stats`),
};
